import BigNumber from 'bignumber.js';
import { calculatePlatformFeeFromPrimaryAmount } from 'shared/utils/currency';
import { multiply, multiplyByPercent, sum } from 'shared/utils/calculations';

export const calculatePlatformFee = (amount: number | BigNumber, feePercentage: number): number => {
  return +new BigNumber(amount).multipliedBy(feePercentage).dividedBy(100).toFixed(5);
};

export const calculateMatchWinnings = (
  entryFee: number,
  platformFeePercentage = 0,
  wonPlayersCount = 1,
  playersCount = 2
): number => {
  const total = new BigNumber(entryFee).multipliedBy(playersCount);

  return +total
    .minus(calculatePlatformFee(total, platformFeePercentage))
    .dividedBy(wonPlayersCount)
    .toFixed(5);
};

/**
 * @deprecated use `TournamentDistributionSettings`
 */
interface SingleCurrencyTournamentDistributionSettings extends TournamentDistributionSettings {
  platformFeePercentage: number;
  isPlatformFeeInclusiveIntoSeedAmount?: boolean;
}

interface TournamentDistributionSettings {
  teamSize: number;
  minTeams: number;
  maxTeams: number;
  currentTeamsCount: number;
  entryFee: number;
  seedAmount: number;
  hostFeePercentage: number;
  playersToExcludeCount?: number;
}

/**
 * @deprecated not needed with `calculateTournamentPrizes`
 */
export const calculateSeedAmountIncludingPlatformFee = ({
  seedAmount,
  platformFeePercentage,
}: Pick<SingleCurrencyTournamentDistributionSettings, 'seedAmount' | 'platformFeePercentage'>) => {
  const percentageInDecimal = platformFeePercentage / 100;
  return seedAmount / (1 - percentageInDecimal);
};

export const calculateSingleCurrencyTournamentPrizes = ({
  teamSize,
  minTeams,
  maxTeams,
  currentTeamsCount,
  entryFee,
  seedAmount,
  hostFeePercentage,
  platformFeePercentage,
  isPlatformFeeInclusiveIntoSeedAmount = false,
}: SingleCurrencyTournamentDistributionSettings) => {
  const totalSeedAmount = isPlatformFeeInclusiveIntoSeedAmount
    ? calculateSeedAmountIncludingPlatformFee({
        seedAmount,
        platformFeePercentage,
      })
    : seedAmount;

  const minEntryFees = entryFee * teamSize * minTeams;
  const maxEntryFees = entryFee * teamSize * maxTeams;
  const currentEntryFees = entryFee * teamSize * Math.max(minTeams, currentTeamsCount);

  const minHostFee = (minEntryFees * hostFeePercentage) / 100;
  const maxHostFee = (maxEntryFees * hostFeePercentage) / 100;
  const currentHostFee = (currentEntryFees * hostFeePercentage) / 100;

  const minPlatformFee = ((minEntryFees + totalSeedAmount) * platformFeePercentage) / 100;
  const maxPlatformFee = ((maxEntryFees + totalSeedAmount) * platformFeePercentage) / 100;
  const currentPlatformFee = ((currentEntryFees + totalSeedAmount) * platformFeePercentage) / 100;

  const minPlayerPrize = totalSeedAmount + minEntryFees - minHostFee - minPlatformFee;
  const maxPlayerPrize = totalSeedAmount + maxEntryFees - maxHostFee - maxPlatformFee;
  const currentPlayerPrize =
    totalSeedAmount + currentEntryFees - currentHostFee - currentPlatformFee;

  const minHostTotal = minHostFee - totalSeedAmount;
  const maxHostTotal = maxHostFee - totalSeedAmount;

  return {
    entryFees: {
      min: minEntryFees,
      max: maxEntryFees,
    },
    playerPrizePool: {
      min: minPlayerPrize,
      max: maxPlayerPrize,
      current: currentPlayerPrize,
    },
    hostFee: {
      min: minHostFee,
      max: maxHostFee,
    },
    platformFee: {
      min: minPlatformFee,
      max: maxPlatformFee,
    },
    hostTotal: {
      min: minHostTotal,
      max: maxHostTotal,
    },
  };
};

export const calculateTournamentPrizes = ({
  teamSize,
  minTeams,
  maxTeams,
  currentTeamsCount,
  entryFee,
  seedAmount,
  hostFeePercentage,
  playersToExcludeCount = 0,
}: TournamentDistributionSettings) => {
  const minPlayersCount = sum(multiply(teamSize, minTeams), -playersToExcludeCount);
  const maxPlayersCount = sum(multiply(teamSize * maxTeams), -playersToExcludeCount);
  const currentPlayersCount = sum(
    multiply(teamSize, Math.max(minTeams, currentTeamsCount)),
    -playersToExcludeCount
  );

  const minEntryFees = multiply(entryFee, minPlayersCount);
  const maxEntryFees = multiply(entryFee, maxPlayersCount);
  const currentEntryFees = multiply(entryFee, currentPlayersCount);

  const minHostFee = multiplyByPercent(minEntryFees, hostFeePercentage);
  const maxHostFee = multiplyByPercent(maxEntryFees, hostFeePercentage);
  const currentHostFee = multiplyByPercent(currentEntryFees, hostFeePercentage);

  const minPlayerPrize = sum(seedAmount, minEntryFees, -minHostFee);
  const maxPlayerPrize = sum(seedAmount, maxEntryFees, -maxHostFee);
  const currentPlayerPrize = sum(seedAmount, currentEntryFees, -currentHostFee);

  const minHostTotal = sum(minHostFee, -seedAmount);
  const maxHostTotal = sum(maxHostFee, -seedAmount);

  return {
    entryFees: {
      min: minEntryFees,
      max: maxEntryFees,
    },
    playerPrizePool: {
      min: minPlayerPrize,
      max: maxPlayerPrize,
      current: currentPlayerPrize,
    },
    hostFee: {
      min: minHostFee,
      max: maxHostFee,
    },
    hostTotal: {
      min: minHostTotal,
      max: maxHostTotal,
    },
  };
};

interface TournamentPlatformFeesSettings {
  teamSize: number;
  minTeams: number;
  maxTeams: number;
  entryFee: number;
  seedAmount: number;
  feePercentage: number;
  exchangeRate: number;
}

export const calculateTournamentPlatformFees = ({
  teamSize,
  minTeams,
  maxTeams,
  entryFee,
  seedAmount,
  feePercentage,
  exchangeRate,
}: TournamentPlatformFeesSettings) => {
  const platformEntryFee = calculatePlatformFeeFromPrimaryAmount(
    entryFee,
    exchangeRate,
    feePercentage
  );

  const totalMinPlatformEntryFees = teamSize * platformEntryFee * minTeams;
  const totalMaxPlatformEntryFees = teamSize * platformEntryFee * maxTeams;

  const seedPlatformFee = calculatePlatformFeeFromPrimaryAmount(
    seedAmount,
    exchangeRate,
    feePercentage
  );

  return {
    platformEntryFee,
    totalEntryPlatformFees: {
      min: totalMinPlatformEntryFees,
      max: totalMaxPlatformEntryFees,
    },
    seedPlatformFee,
  };
};
